/* eslint-env browser */
import React from "react";
import { BrowserRouter, Routes, Route, } from 'react-router-dom'
import LandingPage from "./views/LandingPage.js";
import LoginPage from "./views/LoginPage.js";
import PrivacyPolicy from "./views/PrivacyPolicy.js";
import AboutUs from "./views/AboutUs";
import AuthLoading from './views/AuthLoading';
import { Provider } from "react-redux";
import ProtectedRoute from './views/ProtectedRoute';
import MyProfile from './views/MyProfile';
import BookingHistory from './views/BookingHistory';
import Dashboard from './views/Dashboard';
import CarTypes from './views/CarTypes';
import AddBookings from './views/AddBookings';
import Promos from './views/Promos';
import Users from './views/Users';
import CustomerDetails from "views/CustomerDetails.js";
import Notifications from './views/Notifications';
import Settings from './views/Settings.js';
import Complain from "views/Complain.js";
import AddMoney from "./views/AddMoney";
import Withdraws from './views/Withdraws';
import AllReports from "./views/AllReports";
import { FirebaseProvider, store } from "common";
import { FirebaseConfig } from './config/FirebaseConfig';
import { GoogleMapApiConfig } from './config/GoogleMapApiConfig';
import { GoogleMapApiOptions } from './mapConfig';
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import ContactUs from "./views/ContactUs";
import UserWallet from "./views/UserWallet";
import CarsList from "./views/CarsList";
// One theme for the whole portal (L2). The @mui/material ThemeProvider also
// feeds the legacy @mui/styles makeStyles/withStyles consumers.
import { ThemeProvider } from '@mui/material/styles';
import GlobalStyles from '@mui/material/GlobalStyles';
import theme, { portalGlobalStyles } from './theme';
import { FeedbackProvider } from './components/feedback';
import { useJsApiLoader } from '@react-google-maps/api';
import TermCondition from "views/TermCondition.js";
import { GoogleOAuthProvider } from '@react-oauth/google';
import { webClientId } from "config/ClientIds.js";
import { HelmetProvider } from "react-helmet-async";
import Sos from './views/Sos';
import DriverDetails from "views/DriverDetails.js";
import Error from "views/Error.js";
import BookingDetails from "views/BookingDetails.js";
import UserDocuments from "views/UserDocuments.js";
import AddNotifications from "views/AddNotifications.js";
import EditPromo from "views/EditPromo.js";
import EditCar from "views/EditCar.js";
import EditCarType from "views/EditCarType.js";
import EditUser from "views/EditUser.js";
import PaymentSettings from "views/PaymentSettings.js";
import UnifiedDriverOnboarding from "views/UnifiedDriverOnboarding.js";
import PasswordProtection from "./components/PasswordProtection";
import TransactionLogs from "views/TransactionLogs.js";
import EventPayments from "views/EventPayments.js";
import ReleaseNotes from "views/ReleaseNotes.js";
import DriverDocuments from "views/DriverDocuments.js";
import SecurityDashboard from "views/SecurityDashboard.js";
import CityEvents from "views/CityEvents.js";
import DriverIncentiveDashboard from "views/DriverIncentiveDashboard.js";
import AgentPerformanceDashboard from "views/AgentPerformanceDashboard.js";
import PhoneCards from "views/PhoneCards.js";
import Ngos from "views/Ngos.js";
import EditNgo from "views/EditNgo.js";
import NgoDonations from "views/NgoDonations.js";
import { useStaffActivityTracker } from "services/staffActivityLogger";

// Thin wrapper that mounts the staff-activity tracker hook. Has to live
// INSIDE BrowserRouter so useLocation() works, and INSIDE Provider so
// useSelector() works. Renders its children unchanged.
function StaffActivityTracker({ children }) {
  useStaffActivityTracker();
  return children;
}
// Use the libraries defined in GoogleMapApiOptions
const libraries = GoogleMapApiOptions.libraries;

// Load Discord test utilities in development mode
if (process.env.NODE_ENV === 'development') {
  import('./utils/testDiscordNotifications').then(() => {
    console.log('Discord test utilities loaded! Use testDiscord in console.');
  }).catch(err => {
    console.log('Discord test utilities not available:', err.message);
  });
}

i18n
.use(initReactI18next) 
.init({
    resources: {},
    fallbackLng: "en",
    ns: ["translations"],
    defaultNS: "translations",
    interpolation: {
        escapeValue: false
    }
});

// Keep <html lang> on the language in use, so screen readers pick the right
// voice and the browser picks the right Ethiopic or Latin font.
i18n.on('languageChanged', (lng) => {
  try {
    if (lng) document.documentElement.setAttribute('lang', lng);
  } catch (e) {
    // no document (tests)
  }
});

function App() {

  useJsApiLoader({
    id: 'google-map',
    googleMapsApiKey: GoogleMapApiConfig,
    version: GoogleMapApiOptions.v,
    libraries,
    mapIds: GoogleMapApiOptions.mapIds,
    loadingOptions: {
      async: true
    }
  })

  return (
    <HelmetProvider>
    <Provider store={store}>
      <FirebaseProvider config={FirebaseConfig}>
        <GoogleOAuthProvider clientId={webClientId}>
        <ThemeProvider theme={theme}>
          <GlobalStyles styles={portalGlobalStyles} />
          <FeedbackProvider>
          <AuthLoading>
          <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
            <StaffActivityTracker>
              <Routes>
                <Route path="/dashboard" element={<ProtectedRoute permit={"admin,fleetadmin,callcenteragent,callcentersupervisor,finance,marketing"}><Dashboard /></ProtectedRoute>}/>
                <Route path="/bookings" element={<ProtectedRoute permit={"customer,admin,driver,fleetadmin,finance,callcenteragent,callcentersupervisor"}><BookingHistory /></ProtectedRoute>}/>
                <Route path="/bookings/bookingdetails/:id" element={<ProtectedRoute permit={"customer,admin,driver,fleetadmin,finance,callcentersupervisor,callcenteragent"}><BookingDetails /></ProtectedRoute>}/>
                <Route path="/profile" element={<ProtectedRoute permit={"customer,admin,driver,finance,fleetadmin,callcentersupervisor,callcenteragent"}><MyProfile /></ProtectedRoute>}/>
                <Route path="/cartypes" element={<ProtectedRoute permit={"admin,finance"}><CarTypes /></ProtectedRoute>}/>
                <Route path="/cars" element={<ProtectedRoute permit={"admin,fleetadmin,callcentersupervisor,driver,finance,callcenteragent"}><CarsList /></ProtectedRoute>}/>
                <Route path="/cars/editcar" element={<ProtectedRoute permit={"admin,fleetadmin,driver,callcentersupervisor,callcenteragent"}><EditCar/></ProtectedRoute>}/>
                <Route path="/cars/editcar/:id" element={<ProtectedRoute permit={"admin,fleetadmin,driver,callcentersupervisor,callcenteragent"}><EditCar/></ProtectedRoute>}/>
                <Route path="/addbookings" element={<ProtectedRoute permit={"admin,fleetadmin,customer,finance,callcenteragent,callcentersupervisor"}><AddBookings /></ProtectedRoute>}/>
                <Route path="/promos" element={<ProtectedRoute permit={"admin,marketing"}><Promos /></ProtectedRoute>}/>
                <Route path="/users/:id" element={<ProtectedRoute permit={"admin,fleetadmin,finance,callcentersupervisor,callcenteragent"}><Users /></ProtectedRoute>}/>
                <Route path="/users/customerdetails/:id" element={<ProtectedRoute permit={"admin,fleetadmin,finance,callcenteragent,callcentersupervisor"}><CustomerDetails/></ProtectedRoute>}/>
                <Route path="/users/driverdetails/:id" element={<ProtectedRoute permit={"admin,fleetadmin,finance,callcentersupervisor,callcenteragent"}><DriverDetails/></ProtectedRoute>}/>
                <Route path="/users/userdocuments/:rId/:id" element={<ProtectedRoute permit={"admin,finance,fleetadmin,callcentersupervisor,callcenteragent"}><UserDocuments/></ProtectedRoute>} />
                <Route path="/notifications" element={<ProtectedRoute permit={"admin,marketing,callcentersupervisor"}><Notifications /></ProtectedRoute>}/>
                <Route path="/addtowallet" element={<ProtectedRoute permit={"admin,finance,callcentersupervisor"}><AddMoney /></ProtectedRoute>}/>
                <Route path="/userwallet" element={<ProtectedRoute permit={"customer,driver,finance,accountant,fleetadmin"}><UserWallet /></ProtectedRoute>}/>
                <Route path="/withdraws" element={<ProtectedRoute permit={"admin,finance"}><Withdraws /></ProtectedRoute>}/>
                <Route path="/transactionlogs" element={<ProtectedRoute permit={"admin,finance"}><TransactionLogs /></ProtectedRoute>}/>
                <Route path="/eventpayments" element={<ProtectedRoute permit={"admin,finance,callcentersupervisor"}><EventPayments /></ProtectedRoute>}/>
                <Route path="/sos" element={<ProtectedRoute permit={"admin,callcenteragent,callcentersupervisor"}><Sos /></ProtectedRoute>}/>
                <Route path="/complain" element={<ProtectedRoute permit={"admin,callcenteragent,callcentersupervisor"}><Complain /></ProtectedRoute>}/>
                <Route path="/allreports" element={<ProtectedRoute permit={"admin,finance,accountant,fleetadmin,callcenteragent,callcentersupervisor"}><AllReports /></ProtectedRoute>}/>
                <Route path="/settings" element={<ProtectedRoute permit={"admin"}><Settings /></ProtectedRoute>}/>
                <Route path="/security" element={<ProtectedRoute permit={"admin"}><SecurityDashboard /></ProtectedRoute>}/>
                <Route path="/cityevents" element={<ProtectedRoute permit={"admin,callcentersupervisor,callcenteragent"}><CityEvents /></ProtectedRoute>}/>
                <Route path="/paymentsettings" element={<ProtectedRoute permit={"admin"}><PaymentSettings /></ProtectedRoute>}/>
                <Route path="/releasenotes" element={<ProtectedRoute permit={"admin,finance,callcenteragent,callcentersupervisor"}><ReleaseNotes /></ProtectedRoute>}/>
                <Route path="/driverletter" element={<ProtectedRoute permit={"admin,finance,callcentersupervisor,callcenteragent,fleetadmin"}><DriverDocuments /></ProtectedRoute>}/>
                <Route path="/driverincentive" element={<ProtectedRoute permit={"admin,finance,callcentersupervisor"}><DriverIncentiveDashboard /></ProtectedRoute>}/>
                <Route path="/agentperformance" element={<ProtectedRoute permit={"admin,callcentersupervisor,finance"}><AgentPerformanceDashboard /></ProtectedRoute>}/>
                <Route path="/contact-us" element={<ContactUs />} />
                <Route path="/about-us" element={<AboutUs />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/term-condition" element={<TermCondition />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/oromia-bank" element={<LoginPage path="/oromia-bank" />} />
                <Route path="/users/edituser/:usertype" element={<ProtectedRoute permit={"admin,fleetadmin,callcentersupervisor,callcenteragent"}><EditUser/></ProtectedRoute>}/>
                <Route path="/users/edituser/:usertype/:id" element={<ProtectedRoute permit={"admin,fleetadmin,callcenteragent,callcentersupervisor"}><EditUser/></ProtectedRoute>}/>
                <Route path="/onboard-driver" element={<ProtectedRoute permit={"admin,callcentersupervisor,callcenteragent,scout"}><UnifiedDriverOnboarding/></ProtectedRoute>}/>
                <Route path="/notifications/addnotifications" element={<ProtectedRoute permit={"admin,marketing,callcentersupervisor"}><AddNotifications/></ProtectedRoute>}/>
                <Route path="/promos/editpromo" element={<ProtectedRoute permit={"admin,marketing"}><EditPromo/></ProtectedRoute>}/>
                <Route path="/promos/editpromo/:id" element={<ProtectedRoute permit={"admin,marketing"}><EditPromo/></ProtectedRoute>}/>
                <Route path="/contact-us" element={<ContactUs />} />
                <Route path="/cartypes/editcartype" element={<ProtectedRoute permit={"admin"} ><EditCarType/></ProtectedRoute>} />
                <Route path="/cartypes/editcartype/:id" element={<ProtectedRoute permit={"admin"}><EditCarType/></ProtectedRoute>}/>
                <Route path="/phonecards" element={<ProtectedRoute permit={"admin"}><PhoneCards /></ProtectedRoute>}/>
                <Route path="/ngos" element={<ProtectedRoute permit={"admin"}><Ngos /></ProtectedRoute>}/>
                <Route path="/ngos/editngo" element={<ProtectedRoute permit={"admin"}><EditNgo/></ProtectedRoute>}/>
                <Route path="/ngos/editngo/:id" element={<ProtectedRoute permit={"admin"}><EditNgo/></ProtectedRoute>}/>
                <Route path="/miledonations" element={<ProtectedRoute permit={"admin,finance"}><NgoDonations /></ProtectedRoute>}/>
                <Route path="/*" element={<Error/>} />
                <Route path="/" element={<LandingPage />} />
              </Routes>
            </StaffActivityTracker>
            </BrowserRouter>
          </AuthLoading>
          </FeedbackProvider>
        </ThemeProvider>
        </GoogleOAuthProvider>
      </FirebaseProvider>
    </Provider>
    </HelmetProvider>
  );
}

export default App;